/**
 * Risk Classification Rationale Nomenclature (CRUD).
 * PRD: "Risk Assessment Rationale — Free text (CRUD nomenclature). Entries saved to a shared pool, reuse across entities."
 * One document per organization: risk_classification_rationale/{organizationId} with items array.
 */

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const COLLECTION = 'risk_classification_rationale';

/**
 * Get rationale options for an organization.
 * @param {string} organizationId
 * @returns {Promise<Array<{ id, label, displayOrder }>>}
 */
export async function getRationaleOptions(organizationId) {
  if (!organizationId) return [];
  try {
    const ref = doc(db, COLLECTION, organizationId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return [];
    const data = snap.data();
    const items = data.items ?? [];
    return items
      .slice()
      .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999))
      .map((item) => ({
        id: item.id,
        label: item.label ?? '',
        displayOrder: item.displayOrder ?? 999
      }));
  } catch (err) {
    console.error('[riskClassificationRationaleService] getRationaleOptions:', err);
    throw err;
  }
}

/**
 * Save full list of rationale options (replace). Used by add/update/delete.
 * @param {string} organizationId
 * @param {Array<{ id, label, displayOrder }>} items
 * @param {string} userId
 */
export async function saveRationaleOptions(organizationId, items, userId) {
  if (!organizationId || !userId) throw new Error('organizationId and userId are required');
  try {
    const ref = doc(db, COLLECTION, organizationId);
    await setDoc(
      ref,
      {
        organizationId,
        items: items.map((item) => ({
          id: item.id,
          label: String(item.label ?? '').trim(),
          displayOrder: item.displayOrder ?? 999
        })),
        updatedAt: serverTimestamp(),
        updatedBy: userId
      },
      { merge: true }
    );
  } catch (err) {
    console.error('[riskClassificationRationaleService] saveRationaleOptions:', err);
    throw err;
  }
}

/**
 * Add a new rationale option.
 * @param {string} organizationId
 * @param {string} label
 * @param {string} userId
 * @returns {Promise<string>} new option id
 */
export async function addRationaleOption(organizationId, label, userId) {
  const options = await getRationaleOptions(organizationId);
  const id = `opt_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  const newOption = { id, label: String(label || '').trim(), displayOrder: options.length };
  await saveRationaleOptions(organizationId, [...options, newOption], userId);
  return id;
}

/**
 * Update an existing rationale option.
 */
export async function updateRationaleOption(organizationId, optionId, label, userId) {
  const options = await getRationaleOptions(organizationId);
  const idx = options.findIndex((o) => o.id === optionId);
  if (idx === -1) throw new Error('Option not found');
  options[idx] = { ...options[idx], label: String(label ?? '').trim() };
  await saveRationaleOptions(organizationId, options, userId);
}

/**
 * Delete a rationale option.
 */
export async function deleteRationaleOption(organizationId, optionId, userId) {
  const options = await getRationaleOptions(organizationId).then((list) =>
    list.filter((o) => o.id !== optionId)
  );
  await saveRationaleOptions(organizationId, options, userId);
}

export const riskClassificationRationaleService = {
  getRationaleOptions,
  saveRationaleOptions,
  addRationaleOption,
  updateRationaleOption,
  deleteRationaleOption
};
